/**
 * RsDataTable Component
 *
 * Self-contained table for routine surveillance data. Owns the server-side
 * paginated data fetching, the country selector, column filters, and the
 * pagination + page-size toolbar controls, then renders the current page in a
 * plain (non-virtualized) table.
 *
 * The parent interface only chooses the surveillance level (National vs
 * Subnational) and the surrounding page chrome; everything about loading and
 * presenting a page of rows lives here.
 */

import { useCallback, useEffect, useRef, useState } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react"
import { Spinner } from "@/components/ui/spinner"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import { TruncatedCell } from "@/components/shared/TruncatedCell"
import { SpeciesLabel } from "@/components/shared/SpeciesLabel"
import { useAuth } from "@/hooks/useAuth"
import {
  getRoutineSurveillanceData,
  getRoutineSurveillanceFacets,
  RoutineSurveillanceFilters,
  RoutineSurveillanceRow,
  RoutineSurveillanceTopic,
} from "@/services/routineSurveillanceService"
import { getIso3ToCountryMap } from "@/services/mapAdminInfoService"
import TableFiltersSelector, { FilterOption } from "@/components/shared/TableFiltersSelector"
import SingleSelectCombobox, { ComboboxOption } from "@/components/shared/SingleSelectCombobox"
import {
  ALL_ADMIN_UNITS_TOKEN,
  ALL_COUNTRIES_TOKEN,
  AdminUnitOption,
  fetchAdminUnitOptions,
} from "@/components/rs-data/rsPlotData"
import { Link } from "react-router-dom"

/**
 * Column definition for the routine surveillance data table.
 * Each entry maps a key from RoutineSurveillanceRow to a display label.
 */
export interface RsColumn {
  /** The key on RoutineSurveillanceRow this column displays. */
  key: keyof RoutineSurveillanceRow
  /** Header label text. */
  label: string
}

/**
 * Ordered column definitions rendered in the table. Exported so other RS views
 * (e.g. the full-page graph's data table) render the exact same columns, in the
 * same order, with the same labels — a single source of truth for the column set.
 */
export const RS_COLUMNS: RsColumn[] = [
  { key: "iso3", label: "ISO3" },
  { key: "metric_name", label: "Metric" },
  { key: "value", label: "Value" },
  { key: "start_date", label: "Start Date" },
  { key: "end_date", label: "End Date" },
  { key: "facility_type", label: "Facility Type" },
  { key: "sector", label: "Sector" },
  { key: "origin_name", label: "Origin" },
  { key: "case_detection_name", label: "Case Detection" },
  { key: "country_name", label: "Country" },
  { key: "admin_unit_name", label: "Admin Unit" },
  { key: "admin_level", label: "Admin Level" },
  { key: "map_admin_id", label: "Admin ID" },
  { key: "pf", label: "Pf" },
  { key: "pv", label: "Pv" },
  { key: "po", label: "Po" },
  { key: "pm", label: "Pm" },
  { key: "pk", label: "Pk" },
  { key: "is_malignant", label: "Malignant" },
  { key: "is_mixed_infection", label: "Mixed Infection" },
  { key: "is_tertian", label: "Tertian" },
  { key: "pregnant_only", label: "Pregnant Only" },
  { key: "sex", label: "Sex" },
  { key: "urbanisation", label: "Urbanisation" },
  { key: "lower_age_years", label: "Lower Age" },
  { key: "upper_age_years", label: "Upper Age" },
  { key: "biblio_id", label: "Biblio ID" },
  { key: "series_id", label: "Series ID" },
  { key: "consolidated_species_code", label: "Species Code" },
]

/** Selectable page sizes for the rows-per-page control. */
const PAGE_SIZE_OPTIONS: number[] = [10, 25, 50, 100, 200]

/** Default number of rows fetched per page. */
const DEFAULT_PAGE_SIZE: number = 50

/**
 * The admin_level value of a national (country-aggregate) row. National rows
 * have admin_level 0; anything above 0 is sub-national. The National view
 * filters admin_level IN [0] and the Subnational view filters admin_level NOT
 * IN [0]. Sent as a string because filter values are serialised as strings
 * (the backend treats admin_level as a numeric column and renders it unquoted).
 */
const NATIONAL_ADMIN_LEVEL: string = "0"

/**
 * Columns that support server-side filtering, with their display labels.
 * These MUST stay in sync with the backend FILTERABLE_COLUMNS allowlist in the
 * routine-surveillance Lambda handler — the backend rejects any column not on
 * its list, so an out-of-sync entry here surfaces as a 400 rather than silent
 * data. Ordered to roughly match the table's column order for familiarity.
 */
const FILTERABLE_COLUMNS: FilterOption[] = [
  // NOTE: `iso3` is intentionally NOT in this list — country selection is
  // handled by the dedicated country dropdown above the table, which owns the
  // `iso3` filter. Exposing it here too would let two controls fight over the
  // same field.
  { key: "metric_name", label: "Metric" },
  { key: "facility_type", label: "Facility Type" },
  { key: "sector", label: "Sector" },
  { key: "origin_name", label: "Origin" },
  { key: "case_detection_name", label: "Case Detection" },
  // admin_level is filterable so users can restrict to a specific admin level
  // beyond the National/Subnational split the country dropdown drives. The
  // country_name/admin_unit_name columns are shown in the table but not offered
  // as filters here.
  { key: "admin_level", label: "Admin Level" },
  { key: "map_admin_id", label: "Admin ID" },
  { key: "sex", label: "Sex" },
  { key: "urbanisation", label: "Urbanisation" },
  { key: "is_malignant", label: "Malignant" },
  { key: "is_mixed_infection", label: "Mixed Infection" },
  { key: "is_tertian", label: "Tertian" },
  { key: "pregnant_only", label: "Pregnant Only" },
  { key: "consolidated_species_code", label: "Species Code" },
  { key: "series_id", label: "Series ID" },
  { key: "biblio_id", label: "Biblio ID" },
]

/**
 * A selectable country in the country dropdown: its ISO3 code (used to filter
 * the data) paired with its display name.
 */
interface CountryOption {
  /** ISO3 code the data is filtered by. */
  iso3: string
  /** Human-readable country name shown in the dropdown. */
  name: string
}

/**
 * Convert the filter Map emitted by TableFiltersSelector into the plain
 * Record<string, string[]> shape the service expects.
 *
 * @param filterMap - Map of column key to the set of selected values.
 * @returns A plain object mapping column key to an array of selected values,
 *          omitting columns with no selections.
 */
function filterMapToRecord(
  filterMap: Map<string, Set<string>>
): RoutineSurveillanceFilters {
  const record: RoutineSurveillanceFilters = {}
  filterMap.forEach((values: Set<string>, key: string) => {
    if (values.size > 0) {
      record[key] = Array.from(values)
    }
  })
  return record
}

/**
 * Convert a Record<string, string[]> of filters into the Map<string, Set>
 * shape TableFiltersSelector uses for its initialFilters prop.
 *
 * @param record - Plain object mapping column key to selected value arrays.
 * @returns An equivalent Map of column key to a Set of selected values.
 */
function filterRecordToMap(
  record: RoutineSurveillanceFilters
): Map<string, Set<string>> {
  const map = new Map<string, Set<string>>()
  for (const [key, values] of Object.entries(record)) {
    if (values.length > 0) {
      map.set(key, new Set(values))
    }
  }
  return map
}

/** Props for the RsDataTable component. */
interface RsDataTableProps {
  /**
   * Which surveillance level this table shows. Controls the National vs
   * Subnational admin_level constraint applied to the data, and — on
   * subnational — enables the extra Admin Level and Admin Unit selectors.
   * Defaults to "national".
   */
  level?: "national" | "subnational"
  /**
   * The selected topic partition ("incidence" or "mortality"), controlled by
   * the parent (from the URL). Every fetch and every facet load is scoped to
   * this topic, so the table only ever shows one topic's rows. The table does
   * not own this value.
   */
  topic: RoutineSurveillanceTopic
  /**
   * The selected country, controlled by the parent (from the URL). On national
   * this is an ISO3 code or ALL_COUNTRIES_TOKEN ("all"); on subnational it is an
   * ISO3 code or "" (no country chosen — "All Countries" is not offered on the
   * subnational view). The table fetches data for this selection; it does not
   * own the value.
   */
  selectedCountry: string
  /**
   * The selected non-zero admin level as its numeric string (e.g. "1", "2"), or
   * "" when none is chosen. Subnational only; ignored on the national view. When
   * set, the table constrains rows to this admin level; when "" it shows every
   * non-national level. Controlled by the parent (from the URL).
   */
  selectedAdminLevel?: string
  /**
   * The selected admin unit's map_admin_id, or ALL_ADMIN_UNITS_TOKEN ("all") for
   * every unit at the chosen level. Subnational only; ignored on the national
   * view. When a specific unit is chosen the table constrains rows to it.
   * Controlled by the parent (from the URL).
   */
  selectedAdminUnit?: string
  /**
   * Called when the user picks a different country in the table's dropdown.
   * The parent updates the URL, which flows back down as selectedCountry.
   *
   * @param nextCountry - The chosen country: an ISO3 code or ALL_COUNTRIES_TOKEN.
   */
  onCountryChange: (nextCountry: string) => void
  /**
   * Called when the user picks a different admin level (subnational only). The
   * parent updates the URL, which flows back down as selectedAdminLevel.
   *
   * @param nextAdminLevel - The chosen non-zero admin level (numeric string).
   */
  onAdminLevelChange?: (nextAdminLevel: string) => void
  /**
   * Called when the user picks a different admin unit (subnational only). The
   * parent updates the URL, which flows back down as selectedAdminUnit.
   *
   * @param nextAdminUnit - The chosen unit's map_admin_id, or the "all" token.
   */
  onAdminUnitChange?: (nextAdminUnit: string) => void
}

/**
 * Self-contained data table for routine surveillance data.
 *
 * Manages server-side paginated fetching of routine surveillance data and
 * renders the current page directly (no virtualization), along with a toolbar
 * providing a country selector, column filters, pagination controls, and a
 * page-size selector. Pagination and data fetching are handled internally,
 * matching the journal articles lazy-loading pattern.
 *
 * @param props - Component props.
 * @param props.level - Whether to present the National or Subnational view.
 *        Affects the admin_level constraint applied to the data. Defaults to
 *        "national".
 */
export default function RsDataTable({
  level = "national",
  topic,
  selectedCountry,
  selectedAdminLevel = "",
  selectedAdminUnit = ALL_ADMIN_UNITS_TOKEN,
  onCountryChange,
  onAdminLevelChange,
  onAdminUnitChange,
}: RsDataTableProps): React.JSX.Element {
  const { getAccessToken } = useAuth()

  // Whether this is the subnational view — enables the Admin Level + Admin Unit
  // selectors and switches the data constraint from "national rows only" to
  // "the chosen non-zero level / unit".
  const isSubnational: boolean = level === "subnational"

  // Whether "All Countries" is selected — the (national) table then shows every
  // country's rows (no iso3 constraint) rather than a single country's.
  // Subnational never allows "All Countries", so this is always false there.
  const isAllCountries: boolean =
    !isSubnational && selectedCountry === ALL_COUNTRIES_TOKEN
  // The concrete ISO3 in effect, or null when showing all countries / nothing
  // selected. On subnational an empty selection ("") also resolves to null.
  const activeIso3: string | null =
    isAllCountries || selectedCountry === "" ? null : selectedCountry

  const [rows, setRows] = useState<RoutineSurveillanceRow[]>([])
  // Starts false: no data is fetched until the user selects a country, so the
  // table shows the "select a country" prompt rather than a loading spinner.
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Pagination state — pageIndex is 0-based internally, 1-based for the API.
  const [pageIndex, setPageIndex] = useState<number>(0)
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE)
  // Mirrors pageSize so the country/filter effect can read the latest page size
  // at fetch time without taking pageSize as a dependency (which would make it
  // re-fetch on every size change, doubling up with handlePageSizeChange).
  const pageSizeRef = useRef<number>(DEFAULT_PAGE_SIZE)
  const [totalPages, setTotalPages] = useState<number>(1)
  const [totalCount, setTotalCount] = useState<number>(0)

  // The partition (snapshot version) resolved by the backend on first fetch.
  const [partition, setPartition] = useState<string | null>(null)

  // Active include filters (column -> values, SQL IN). Includes the iso3
  // country filter, any dialog-selected filters, and — on the National view —
  // the admin_level = 0 constraint pinning results to national rows.
  const [filters, setFilters] = useState<RoutineSurveillanceFilters>({})

  // Active exclude filters (column -> values, SQL NOT IN). On the Subnational
  // view this holds admin_level 0 so national rows are excluded, leaving only
  // sub-national rows.
  const [excludeFilters, setExcludeFilters] = useState<RoutineSurveillanceFilters>({})

  // Country dropdown options, derived from the ISO3 -> countryName mapping and
  // sorted alphabetically. The SELECTION itself is controlled by the parent via
  // the selectedCountry prop (from the URL), not owned here.
  const [countryOptions, setCountryOptions] = useState<CountryOption[]>([])

  // (Subnational only) The non-zero admin levels available in the snapshot for
  // the current topic, as numeric strings sorted ascending. National level 0 is
  // stripped so the dropdown only offers sub-national levels. The SELECTION is
  // controlled by the parent (selectedAdminLevel).
  const [adminLevelOptions, setAdminLevelOptions] = useState<string[]>([])

  // (Subnational only) The admin units available for the selected country +
  // admin level, fetched on demand. The SELECTION is controlled by the parent
  // (selectedAdminUnit); "All Admin Units" is a synthetic option prepended in
  // the combobox, not stored here.
  const [adminUnitOptions, setAdminUnitOptions] = useState<AdminUnitOption[]>([])
  // Whether the admin unit options are currently being fetched, so the Admin
  // Unit dropdown can show a disabled/loading state rather than a stale list.
  const [adminUnitsLoading, setAdminUnitsLoading] = useState<boolean>(false)

  // Whether the one-time initial load of the country dropdown options (and the
  // ISO3 -> ADMIN0 id map) is still in flight. The page is held on a loading
  // spinner until this resolves, so the country dropdown is never shown empty
  // while its contents are still being fetched.
  const [optionsLoading, setOptionsLoading] = useState<boolean>(true)

  // Set if the initial options load fails outright. Surfaced as a page-level
  // error (with retry) instead of silently leaving the dropdown empty.
  const [optionsError, setOptionsError] = useState<string | null>(null)

  // The dialog-selected column filters (excludes the country/admin constraint,
  // which is derived from selectedCountry). Kept so the country effect can
  // rebuild the full filter set on top of them.
  const [dialogFilters, setDialogFilters] = useState<RoutineSurveillanceFilters>({})

  // Selectable option values per filterable column, loaded lazily from the
  // facets endpoint the first time the user opens the filter dialog. Columns
  // absent from this map simply show no options yet (still loading / not loaded).
  const [facetOptions, setFacetOptions] = useState<Record<string, string[]>>({})

  // Guard so facets are only loaded once (they don't change within a snapshot).
  const facetsLoadedRef = useRef<boolean>(false)

  // Monotonically increasing fetch ID used to discard stale responses. When a
  // new fetch is initiated, fetchIdRef is incremented; if an in-flight fetch
  // completes and its captured ID no longer matches, its results are discarded.
  const fetchIdRef = useRef<number>(0)

  /**
   * Fetch a single page of routine surveillance data.
   *
   * Uses a fetch ID to guard against race conditions: if a newer fetch is
   * initiated while this one is in flight, the stale response is discarded.
   *
   * @param page - Zero-based page index to fetch.
   * @param size - Number of rows per page to request.
   * @param activeFilters - Include filters (SQL IN) to apply to this fetch.
   * @param activeExcludeFilters - Exclude filters (SQL NOT IN) to apply. Both
   *        are passed explicitly (rather than read from state) so callers always
   *        send the exact filters they intend, avoiding stale-closure bugs when
   *        a filter change and a page change race.
   */
  const fetchPage = useCallback(async (
    page: number,
    size: number,
    activeFilters: RoutineSurveillanceFilters,
    activeExcludeFilters: RoutineSurveillanceFilters
  ): Promise<void> => {
    const currentFetchId = ++fetchIdRef.current

    setLoading(true)
    setError(null)
    try {
      const token = await getAccessToken()
      const result = await getRoutineSurveillanceData(token, page + 1, size, {
        topic,
        filters: activeFilters,
        excludeFilters: activeExcludeFilters,
      })

      // Discard stale responses from superseded fetches.
      if (currentFetchId !== fetchIdRef.current) {
        return
      }

      setRows(result.items)
      setTotalCount(result.totalCount)
      setTotalPages(result.totalPages)
      setPartition(result.metadata.snapshot_version)
    } catch (err) {
      if (currentFetchId !== fetchIdRef.current) {
        return
      }
      setError(
        err instanceof Error ? err.message : "Failed to load routine surveillance data"
      )
    } finally {
      if (currentFetchId === fetchIdRef.current) {
        setLoading(false)
      }
    }
  }, [getAccessToken, topic])

  /**
   * Load the one-time country dropdown options: the ISO3 codes present in the
   * RS snapshot intersected with the country-name map.
   *
   * The page is held on a loading spinner (optionsLoading) until this resolves,
   * so the country dropdown is never shown empty while its contents are still
   * being fetched. On failure it sets optionsError so the page shows a
   * retryable error rather than an empty, unusable dropdown. No table data is
   * fetched here — that still waits until the user picks a country.
   *
   * @returns A promise that resolves once the options have loaded (or failed).
   */
  const loadCountryOptions = useCallback(async (): Promise<void> => {
    setOptionsLoading(true)
    setOptionsError(null)

    try {
      const token = await getAccessToken()

      // Fetch the country name map and the set of ISO3 codes that actually
      // appear in the RS snapshot in parallel. The dropdown should only offer
      // countries that have data, so we intersect the two. The national/
      // subnational split is now driven by the admin_level column, so the
      // ISO3 -> ADMIN0 id map is no longer needed here. On subnational we also
      // load the distinct admin_level values so the Admin Level dropdown offers
      // exactly the levels present in the data (minus national level 0).
      const [iso3ToCountry, iso3Facet, adminLevelFacet] = await Promise.all([
        getIso3ToCountryMap(),
        getRoutineSurveillanceFacets(token, "iso3", topic),
        // Only needed for the subnational Admin Level dropdown; skip the extra
        // query on the national view.
        isSubnational
          ? getRoutineSurveillanceFacets(token, "admin_level", topic)
          : Promise.resolve(null),
      ])

      const availableIso3: Set<string> = new Set(iso3Facet.values)

      // Build the subnational admin-level options: every distinct admin_level
      // EXCEPT national (0), sorted numerically ascending so "1, 2, 3" read in
      // order rather than lexically. Left empty on the national view.
      if (adminLevelFacet !== null) {
        const nonNationalLevels: string[] = adminLevelFacet.values
          .filter((levelValue) => levelValue !== NATIONAL_ADMIN_LEVEL)
          .sort((a, b) => Number(a) - Number(b))
        setAdminLevelOptions(nonNationalLevels)
      }

      // Build one option per ISO3 present in the data. If an ISO3 has no name
      // in the admin metadata, fall back to showing the raw code rather than
      // dropping it — otherwise a country with data would silently vanish
      // from the dropdown.
      const options: CountryOption[] = Array.from(availableIso3)
        .map((iso3): CountryOption => ({
          iso3,
          name: iso3ToCountry.get(iso3) ?? iso3,
        }))
        .sort((a, b) => a.name.localeCompare(b.name))

      setCountryOptions(options)
    } catch (err) {
      // Surface the failure as a page-level error (with retry) rather than
      // silently leaving the dropdown empty and unusable.
      setOptionsError(
        err instanceof Error ? err.message : "Failed to load country list"
      )
    } finally {
      setOptionsLoading(false)
    }
  }, [getAccessToken, topic, isSubnational])

  // Initialise on mount, and reload whenever the topic changes: load the
  // country options (which are topic-scoped — only countries with data in the
  // current topic appear). No country is selected by default and NO table data
  // is fetched until the user picks one; the table shows a "select a country"
  // prompt until then. Keyed on loadCountryOptions, which itself depends on
  // topic, so a topic change refreshes the country list.
  useEffect(() => {
    void loadCountryOptions()
  }, [loadCountryOptions])

  /**
   * Apply the National/Subnational admin constraint on top of a set of base
   * include filters, given the current country / admin level / admin unit.
   *
   * The admin_level column identifies a row's administrative level directly:
   * national rows are admin_level 0, sub-national rows are above 0.
   *
   * National view: INCLUDE only national rows (admin_level IN [0]). Applies
   * uniformly, including in "All Countries" mode, so it returns every country's
   * national row.
   *
   * Subnational view: national rows are always excluded. The exact constraint
   * depends on how far the user has drilled down:
   *   - No admin level chosen → EXCLUDE admin_level 0 (all non-national rows).
   *   - An admin level chosen → INCLUDE that admin_level (a single level; since
   *     it is non-zero this already excludes national rows).
   *   - A specific admin unit chosen → additionally INCLUDE its map_admin_id.
   *
   * @param baseFilters - Include filters to build on (dialog filters).
   * @param iso3 - The selected country's ISO3 code, or null when none is chosen
   *        / "All Countries".
   * @param adminLevel - The selected non-zero admin level (numeric string), or
   *        "" when none is chosen. Subnational only.
   * @param adminUnit - The selected admin unit's map_admin_id, or the
   *        "All Admin Units" token / "". Subnational only.
   * @returns The include and exclude filter records to send to the API.
   */
  const applyAdminLevelConstraint = useCallback((
    baseFilters: RoutineSurveillanceFilters,
    iso3: string | null,
    adminLevel: string,
    adminUnit: string
  ): { filters: RoutineSurveillanceFilters; excludeFilters: RoutineSurveillanceFilters } => {
    // Always start from the base filters with any stale country/admin entries
    // stripped, so switching country/level/unit never leaves an old value
    // behind.
    const nextFilters: RoutineSurveillanceFilters = { ...baseFilters }
    delete nextFilters.iso3
    delete nextFilters.admin_level
    delete nextFilters.map_admin_id
    const nextExcludeFilters: RoutineSurveillanceFilters = {}

    // A specific country pins the iso3 filter; null (none chosen / All
    // Countries) leaves it off so every country's rows are considered.
    if (iso3 !== null) {
      nextFilters.iso3 = [iso3]
    }

    if (level === "subnational") {
      if (adminLevel !== "") {
        // A specific non-zero level: include exactly that level. Being non-zero,
        // it inherently excludes national rows, so no exclude filter is needed.
        nextFilters.admin_level = [adminLevel]
        // A specific unit within that level pins its map_admin_id.
        if (adminUnit !== "" && adminUnit !== ALL_ADMIN_UNITS_TOKEN) {
          nextFilters.map_admin_id = [adminUnit]
        }
      } else {
        // No level chosen yet: show all non-national rows by excluding level 0.
        nextExcludeFilters.admin_level = [NATIONAL_ADMIN_LEVEL]
      }
    } else {
      // National: include only national rows.
      nextFilters.admin_level = [NATIONAL_ADMIN_LEVEL]
    }

    return { filters: nextFilters, excludeFilters: nextExcludeFilters }
  }, [level])

  /**
   * Navigate to the next page of results.
   */
  const handleNextPage = useCallback((): void => {
    if (pageIndex >= totalPages - 1) return
    const next = pageIndex + 1
    setPageIndex(next)
    fetchPage(next, pageSize, filters, excludeFilters)
  }, [pageIndex, totalPages, pageSize, fetchPage, filters, excludeFilters])

  /**
   * Navigate to the previous page of results.
   */
  const handlePreviousPage = useCallback((): void => {
    if (pageIndex === 0) return
    const prev = pageIndex - 1
    setPageIndex(prev)
    fetchPage(prev, pageSize, filters, excludeFilters)
  }, [pageIndex, pageSize, fetchPage, filters, excludeFilters])

  /**
   * Jump to the first page of results.
   */
  const handleFirstPage = useCallback((): void => {
    if (pageIndex === 0) return
    setPageIndex(0)
    fetchPage(0, pageSize, filters, excludeFilters)
  }, [pageIndex, pageSize, fetchPage, filters, excludeFilters])

  /**
   * Jump to the last page of results.
   */
  const handleLastPage = useCallback((): void => {
    if (pageIndex >= totalPages - 1) return
    const last = totalPages - 1
    setPageIndex(last)
    fetchPage(last, pageSize, filters, excludeFilters)
  }, [pageIndex, totalPages, pageSize, fetchPage, filters, excludeFilters])

  /**
   * Change the number of rows per page and reset to the first page.
   *
   * @param value - The selected page size as a string (from the Select component).
   */
  const handlePageSizeChange = useCallback((value: string): void => {
    const parsed: number = parseInt(value, 10)
    if (!PAGE_SIZE_OPTIONS.includes(parsed)) return
    setPageSize(parsed)
    pageSizeRef.current = parsed
    setPageIndex(0)
    fetchPage(0, parsed, filters, excludeFilters)
  }, [fetchPage, filters, excludeFilters])

  /**
   * Apply a new set of column filters: reset to the first page and re-fetch.
   *
   * Called when the user saves selections in the filter dialog. Resetting to
   * page 0 is required because the filtered result set has a different size and
   * the current page index may point past its end.
   *
   * @param filterMap - The filter selections from TableFiltersSelector.
   */
  const handleApplyFilters = useCallback((filterMap: Map<string, Set<string>>): void => {
    // Record the dialog filters; the country effect (below) rebuilds the
    // effective filters on top of them and fetches. Reset to page 0 since the
    // filtered result set has a different size.
    setDialogFilters(filterMapToRecord(filterMap))
    setPageIndex(0)
  }, [])

  /**
   * Change the selected country: set the `iso3` filter to the chosen country's
   * code, reset to the first page, and re-fetch.
   *
   * The country selection is modelled as the `iso3` column filter. Any other
   * active column filters (set via the filter dialog) are preserved; only the
   * `iso3` entry is replaced. An empty string (the combobox's clear action)
   * removes the `iso3` filter entirely so all countries are shown.
   *
   * @param iso3 - The ISO3 code of the newly selected country, or "" when
   *        the selection is cleared.
   */
  const handleCountryChange = useCallback((nextCountry: string): void => {
    // Selection is controlled by the parent (URL). Report the change up; the
    // new value flows back down as selectedCountry and the effect re-fetches.
    // Guard against no-op re-selection of the current value.
    if (nextCountry !== "" && nextCountry !== selectedCountry) {
      onCountryChange(nextCountry)
    }
  }, [onCountryChange, selectedCountry])

  /**
   * Change the selected admin level (subnational only). Reports the change up
   * to the parent, which updates the URL; the new value flows back down as
   * selectedAdminLevel and the effect re-fetches. No-op re-selection is ignored.
   *
   * @param nextAdminLevel - The chosen non-zero admin level (numeric string).
   */
  const handleAdminLevelChange = useCallback((nextAdminLevel: string): void => {
    if (nextAdminLevel !== "" && nextAdminLevel !== selectedAdminLevel) {
      onAdminLevelChange?.(nextAdminLevel)
    }
  }, [onAdminLevelChange, selectedAdminLevel])

  /**
   * Change the selected admin unit (subnational only). Reports the change up to
   * the parent, which updates the URL; the new value flows back down as
   * selectedAdminUnit and the effect re-fetches. No-op re-selection is ignored.
   *
   * @param nextAdminUnit - The chosen unit's map_admin_id, or the "all" token.
   */
  const handleAdminUnitChange = useCallback((nextAdminUnit: string): void => {
    if (nextAdminUnit !== "" && nextAdminUnit !== selectedAdminUnit) {
      onAdminUnitChange?.(nextAdminUnit)
    }
  }, [onAdminUnitChange, selectedAdminUnit])

  // (Subnational only) Load the admin unit options whenever the country + admin
  // level selection resolves to a concrete pair. Cleared when either is missing
  // so a stale unit list from a previous country/level is never shown. Guarded
  // by a fetch id so a slower earlier response can't overwrite a newer one.
  const adminUnitsFetchIdRef = useRef<number>(0)
  useEffect(() => {
    // Only meaningful on subnational, and only once both country and level are
    // chosen — until then there is nothing to enumerate.
    if (!isSubnational || activeIso3 === null || selectedAdminLevel === "") {
      setAdminUnitOptions([])
      setAdminUnitsLoading(false)
      return
    }

    const currentFetchId: number = ++adminUnitsFetchIdRef.current
    setAdminUnitsLoading(true)

    void (async (): Promise<void> => {
      try {
        const token = await getAccessToken()
        const { units } = await fetchAdminUnitOptions(
          token,
          activeIso3,
          selectedAdminLevel,
          topic
        )
        // Discard a stale response superseded by a newer country/level change.
        if (currentFetchId !== adminUnitsFetchIdRef.current) return
        setAdminUnitOptions(units)
      } catch (err) {
        if (currentFetchId !== adminUnitsFetchIdRef.current) return
        // Fail loud in logs; leave the options empty so the dropdown offers only
        // "All Admin Units" rather than a stale/partial list.
        console.error("Failed to load admin unit options:", err)
        setAdminUnitOptions([])
      } finally {
        if (currentFetchId === adminUnitsFetchIdRef.current) {
          setAdminUnitsLoading(false)
        }
      }
    })()
  }, [isSubnational, activeIso3, selectedAdminLevel, topic, getAccessToken])

  // Drive the effective filters + fetch from the controlled selections (country,
  // and — subnational — admin level + unit), the level, and the dialog filters.
  // Runs on mount and whenever any of those change, so the URL's selection is
  // always reflected in the data. Reset to page 0 because a new selection has a
  // different-sized result set.
  useEffect(() => {
    // Gating: don't fetch until there is enough of a selection to be meaningful.
    // National waits for a country (or All Countries). Subnational waits for
    // BOTH a country and an admin level; until then the table shows a prompt
    // rather than firing a query that would return the whole (or wrong) set.
    const nationalReady: boolean = !isSubnational && selectedCountry !== ""
    const subnationalReady: boolean =
      isSubnational && activeIso3 !== null && selectedAdminLevel !== ""
    if (!nationalReady && !subnationalReady) {
      return
    }

    const { filters: nextFilters, excludeFilters: nextExcludeFilters } =
      applyAdminLevelConstraint(
        dialogFilters,
        activeIso3,
        selectedAdminLevel,
        selectedAdminUnit
      )
    setFilters(nextFilters)
    setExcludeFilters(nextExcludeFilters)
    setPageIndex(0)
    // Read the current pageSize at fetch time. pageSize is intentionally not a
    // dependency: changing page size has its own handler that re-fetches, so
    // including it here would double-fetch on size change.
    fetchPage(0, pageSizeRef.current, nextFilters, nextExcludeFilters)
  }, [
    isSubnational,
    selectedCountry,
    activeIso3,
    selectedAdminLevel,
    selectedAdminUnit,
    dialogFilters,
    applyAdminLevelConstraint,
    fetchPage,
  ])

  /**
   * Lazily load the selectable option values for every filterable column,
   * scoped to the current topic.
   *
   * Runs at most once per topic. Each column's distinct values come from a
   * separate facets query; they're fetched in parallel. A single column's
   * failure does not abort the others — its options simply stay empty and it
   * logs the error, so the dialog remains usable for the columns that did load.
   */
  const loadFacets = useCallback(async (): Promise<void> => {
    if (facetsLoadedRef.current) return
    facetsLoadedRef.current = true
    const token = await getAccessToken()
    const results = await Promise.all(
      FILTERABLE_COLUMNS.map(async (col): Promise<[string, string[]]> => {
        try {
          // Scope each facet to the current topic so the filter dialog only
          // offers values that exist within the topic being viewed.
          const facet = await getRoutineSurveillanceFacets(token, col.key, topic)
          return [col.key, facet.values]
        } catch (err) {
          // Fail loud in logs, but keep the dialog usable for other columns.
          console.error(`Failed to load filter options for '${col.key}':`, err)
          return [col.key, []]
        }
      })
    )
    setFacetOptions(Object.fromEntries(results))
  }, [getAccessToken, topic])

  // When the topic changes, the previously loaded (topic-scoped) facet options
  // are stale, so reset the one-load guard and clear them. The effect below
  // then reloads them for the new topic on the next resolved fetch.
  useEffect(() => {
    facetsLoadedRef.current = false
    setFacetOptions({})
  }, [topic])

  // Load facet options once per topic, after a page fetch has resolved the
  // partition. This keeps the initial page render fast (facets are a burst of
  // DISTINCT queries) while ensuring options are ready by the time the user
  // opens the filter dialog. Re-runs after a topic change because the reset
  // effect above clears facetsLoadedRef.
  useEffect(() => {
    if (partition !== null && !facetsLoadedRef.current) {
      void loadFacets()
    }
  }, [partition, loadFacets])

  // Build the filterOptions passed to the selector by merging static labels
  // with any loaded option values. On subnational, admin_level is driven by the
  // dedicated Admin Level toolbar dropdown, so it is removed from the in-dialog
  // filters to avoid two controls fighting over the same column.
  const filterOptionsWithValues: FilterOption[] = FILTERABLE_COLUMNS
    .filter(col => !(isSubnational && col.key === "admin_level"))
    .map(col => ({
      ...col,
      options: facetOptions[col.key] ?? [],
    }))

  // Map the country list into the {value,label} shape SingleSelectCombobox
  // expects: value is the ISO3 code (what we filter on), label is the country
  // name (what the user searches and sees). Already sorted alphabetically. The
  // "All Countries" option is prepended only on the national view — subnational
  // requires a specific country, so it is omitted there.
  const countryComboboxOptions: ComboboxOption[] = [
    ...(isSubnational
      ? []
      : [{ value: ALL_COUNTRIES_TOKEN, label: "All Countries" }]),
    ...countryOptions.map(country => ({
      value: country.iso3,
      label: country.name,
    })),
  ]

  // (Subnational) Admin level options in the {value,label} shape. Value is the
  // numeric level string; label prefixes it for readability (e.g. "Level 1").
  const adminLevelComboboxOptions: ComboboxOption[] = adminLevelOptions.map(
    (levelValue) => ({ value: levelValue, label: `Level ${levelValue}` })
  )

  // (Subnational) Admin unit options in the {value,label} shape, with the
  // synthetic "All Admin Units" option prepended (the default selection). Value
  // is the unit's map_admin_id; label is its name.
  const adminUnitComboboxOptions: ComboboxOption[] = [
    { value: ALL_ADMIN_UNITS_TOKEN, label: "All Admin Units" },
    ...adminUnitOptions.map((unit) => ({
      value: unit.mapAdminId,
      label: unit.name,
    })),
  ]

  // Whether the current selection is complete enough for the table to fetch.
  // National needs a country (or All Countries); subnational needs BOTH a
  // country and an admin level. Until then no query fires (see the fetch
  // effect's gating) and the body shows a prompt rather than "No data".
  const selectionReady: boolean = isSubnational
    ? activeIso3 !== null && selectedAdminLevel !== ""
    : selectedCountry !== ""

  // The message shown in the empty table body: a prompt to complete the
  // selection while it is incomplete, otherwise the genuine "no rows" message.
  const emptyStateMessage: string = !selectionReady
    ? isSubnational
      ? "Select a country and admin level to view data."
      : "Select a country to view data."
    : "No data available."

  // Error state: show the message and a retry that re-runs the current fetch.
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 gap-2">
        <p className="text-destructive">{error}</p>
        <button
          onClick={() => fetchPage(pageIndex, pageSize, filters, excludeFilters)}
          className="text-sm underline text-primary hover:text-primary/80"
        >
          Retry
        </button>
      </div>
    )
  }

  // Hold the whole page on a loading spinner until the country dropdown options
  // have been received, so the dropdown is never shown empty/disabled while its
  // contents are still loading.
  if (optionsLoading) {
    return <LoadingSpinner loadingContent="Loading surveillance data..." />
  }

  // If the options failed to load the dropdown would be empty and unusable, so
  // surface a retryable error for the whole page instead.
  if (optionsError !== null) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-8">
        <p className="text-destructive">{optionsError}</p>
        <button
          onClick={() => { void loadCountryOptions() }}
          className="text-sm underline text-primary hover:text-primary/80"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <>
      {/* Toolbar: pagination info + controls on the left, page size on the right */}
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div className="flex gap-1 flex-wrap items-center">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {loading ? (
              <span className="flex items-center gap-2">
                <Spinner className="text-primary" />
                Loading...
              </span>
            ) : totalCount > 0 ? (
              (() => {
                const from: number = pageIndex * pageSize + 1
                const to: number = Math.min((pageIndex + 1) * pageSize, totalCount)
                return `${from.toLocaleString()}–${to.toLocaleString()} of ${totalCount.toLocaleString()} rows`
              })()
            ) : (
              "No data"
            )}
          </span>
          {partition !== null && (
            <span className="text-sm text-muted-foreground whitespace-nowrap ml-3">
              Snapshot:{" "}
              <span className="font-medium text-foreground">{partition}</span>
            </span>
          )}
          <div className="flex items-center gap-1 ml-2">
            <Button
              variant="ghost"
              size="xs"
              className="p-0"
              onClick={handleFirstPage}
              disabled={pageIndex === 0 || loading}
              aria-label="Go to first page"
            >
              <ChevronsLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              className="p-1"
              onClick={handlePreviousPage}
              disabled={pageIndex === 0 || loading}
              aria-label="Go to previous page"
            >
              <ChevronLeft className="size-4" /> Prev
            </Button>
            <Button
              variant="ghost"
              size="xs"
              className="p-1"
              onClick={handleNextPage}
              disabled={pageIndex >= totalPages - 1 || loading}
              aria-label="Go to next page"
            >
              Next <ChevronRight className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="xs"
              className="p-0"
              onClick={handleLastPage}
              disabled={pageIndex >= totalPages - 1 || loading}
              aria-label="Go to last page"
            >
              <ChevronsRight className="size-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Country selector — drives the iso3 filter. Searchable
              combobox sitting just left of the Filters button. */}
          <div className="flex items-center gap-2">
            <label
              htmlFor="rs-country-select"
              className="text-sm text-muted-foreground whitespace-nowrap"
            >
              Country
            </label>
            <div className="w-[240px]">
              <SingleSelectCombobox
                id="rs-country-select"
                options={countryComboboxOptions}
                value={selectedCountry}
                onChange={handleCountryChange}
                placeholder="Select a country"
                emptyMessage="No countries found."
                disabled={countryOptions.length === 0 || loading}
                showClear={false}
              />
            </div>
          </div>

          {/* (Subnational only) Admin Level selector — sits just right of the
              Country selector and constrains rows to one non-zero admin level.
              Offers only the levels present in the data (national level 0 is
              stripped). */}
          {isSubnational && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="rs-admin-level-select"
                className="text-sm text-muted-foreground whitespace-nowrap"
              >
                Admin Level
              </label>
              <div className="w-[150px]">
                <SingleSelectCombobox
                  id="rs-admin-level-select"
                  options={adminLevelComboboxOptions}
                  value={selectedAdminLevel}
                  onChange={handleAdminLevelChange}
                  placeholder="Select a level"
                  emptyMessage="No admin levels found."
                  // A country must be chosen before a level is meaningful.
                  disabled={
                    activeIso3 === null || adminLevelComboboxOptions.length === 0 || loading
                  }
                  showClear={false}
                />
              </div>
            </div>
          )}

          {/* (Subnational only) Admin Unit selector — sits right of Admin Level.
              Lists the admin units within the selected country + level, with an
              "All Admin Units" default. */}
          {isSubnational && (
            <div className="flex items-center gap-2">
              <label
                htmlFor="rs-admin-unit-select"
                className="text-sm text-muted-foreground whitespace-nowrap"
              >
                Admin Unit
              </label>
              <div className="w-[240px]">
                <SingleSelectCombobox
                  id="rs-admin-unit-select"
                  options={adminUnitComboboxOptions}
                  value={selectedAdminUnit}
                  onChange={handleAdminUnitChange}
                  placeholder="Select an admin unit"
                  emptyMessage="No admin units found."
                  // An admin level must be chosen (and its units loaded) first.
                  disabled={selectedAdminLevel === "" || adminUnitsLoading || loading}
                  showClear={false}
                />
              </div>
            </div>
          )}

          <TableFiltersSelector
            filterOptions={filterOptionsWithValues}
            initialFilters={filterRecordToMap(filters)}
            onSave={handleApplyFilters}
          />

          <div className="flex items-center gap-1.5">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Rows</span>
            <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="!h-[32px] w-[70px] text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(size => (
                  <SelectItem key={size} value={String(size)} className="text-sm">
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto border border-border/50 rounded-md">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card shadow-sm">
            <TableRow>
              {RS_COLUMNS.map(col => (
                <TableHead key={col.key} className="text-xs">
                  <SpeciesLabel label={col.label} />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={RS_COLUMNS.length}>
                  <LoadingSpinner loadingContent="Loading routine surveillance data..." />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={RS_COLUMNS.length}
                  className="text-center text-muted-foreground py-8"
                >
                  {emptyStateMessage}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  className="hover:bg-accent/30 transition-colors"
                >
                  {RS_COLUMNS.map(col => (
                    <TableCell key={col.key} className="text-xs max-w-[220px]">
                      {col.key == "biblio_id" ? (
                        <Link to={`/all-sources/${row[col.key]}`} target="_blank">
                          {row[col.key]}
                        </Link>
                      ) : (
                        <TruncatedCell value={row[col.key]} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  )
}
